import { useContext, useEffect, useState } from "react";
import toastMessages from "../../services/utils/helpers/toastMessage";
import useGetTermsAndConditions from "../../services/utils/custom hooks/Documents/useGetTermsAndConditions";
import Button from "../organism/Button";
import { PopUpContext } from "../../services/context/popupContext";
import { useHistory } from "react-router-dom";
import { useUpdateProfileMutation } from "../../services/utils/custom hooks/useUpdateProfile";
import { Formik } from "formik";
import CustomCheckBox from "../atomic/CustomCheckBox";
import moment from "moment";
import useAdBlockDetector from "../global/DetectAdBlock";
import CustomText from "../atomic/CustomText";
import { clearPersistedQueryCache } from "../../services/query/persistedQueryClient";
import { beginLogout, endLogout } from "../../services/session/logoutState";

function TermsAndConditionsPopup({
  oktaAuth,
  id,
  termsAccepted,
  profileData,
  closePopup,
}: Readonly<{
  oktaAuth: any;
  id: string;
  termsAccepted: boolean;
  profileData: any;
  closePopup?: Function;
}>): JSX.Element {
  const [documentLink, setDocumentLink] = useState<string>("");
  const { setVisible, setHideClose } = useContext(PopUpContext);
  const history = useHistory();
  const [cancelLoading, setCancelLoading] = useState(false);
  const { mutation, updating } = useUpdateProfileMutation(oktaAuth);
  const AdBlockerEnabled = useAdBlockDetector();
  
  const getTermsAndConditions = useGetTermsAndConditions();
  const [fetchingDocument, setFetchingDocument] = useState(false);

  const showTermsAndConditions = !termsAccepted && !fetchingDocument;

  useEffect(() => {
    if (AdBlockerEnabled) {
      toastMessages.warning(
        "Ad Blocker Detected. Please disable your ad blocker to use all features of the application"
      );
    }
  }, [AdBlockerEnabled]);

  async function handleViewURL() {
    setFetchingDocument(true);
    try {
      const response = await getTermsAndConditions.mutateAsync(id);
      const data = response.data?.data || [];
      
      let url = "";
      if (id === "isAltheaDxNeuropsychotropicMedications" && data[0]?.blobUrl) {
        url = data[0].blobUrl;
      } else {
        const termOfUse = data.find((file: any) => file?.type === "term-of-use");
        url = termOfUse?.blobUrl || "";
      }

      setDocumentLink(url);

      // If ad blocker is enabled, open the URL in a new tab
      if (AdBlockerEnabled && url) {
        window.open(url, "_blank", "noopener,noreferrer");
      }
      
      setFetchingDocument(false);
    } catch (error: any) {
      toastMessages.error("Failed to open Terms and Conditions");
      setFetchingDocument(false);
    }
  }

  const logout = async () => {
    beginLogout();
    setCancelLoading(true);
    try {
      await clearPersistedQueryCache();
      await oktaAuth?.oktaAuth?.signOut();
      localStorage.clear();
      history.replace("/");
      endLogout();
    } catch (error) {
      endLogout();
      toastMessages.error("Logout failed. Please try again.");
    } finally {
      setCancelLoading(false);
    }
  };

  useEffect(() => {
    if (mutation.isSuccess) {
      setVisible(false);
      setHideClose(false);
      if (closePopup) {
        closePopup(); // Call closePopup to close TermsAndConditionsPopup and open NewReleasePopup
      }
    }
  }, [mutation.isSuccess]);

  useEffect(() => {
    handleViewURL();
  }, []);

  // Mobile detection
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  return (
    <div className="min-w-[35vw] md:h-[80vh] lg:min-w-[30vw] overflow-auto">
      {/* PDF preview for desktop only, not ad-blocked, not mobile */}
      {!fetchingDocument && !!documentLink && !AdBlockerEnabled && !isMobile && (
        <embed
          width={250}
          height={250}
          src={documentLink}
          className={`h-[80vh] ${showTermsAndConditions ? "md:h-[70vh]" : "md:h-[77vh]"} w-full object-contain`}
        />
      )}
      {/* PDF fallback for mobile or ad-blocked */}
      {!fetchingDocument && !!documentLink && (AdBlockerEnabled || isMobile) && (
        <div className="flex flex-col items-center justify-center py-8">
          <Button
            buttonTheme="primary"
            textInformation={{ text: "Open PDF", fontWeight: "font-semibold" }}
            onClick={() => window.open(documentLink, "_blank", "noopener,noreferrer")}
            className="mb-4"
          />
          <CustomText
            text={AdBlockerEnabled
              ? "PDF preview is blocked by your ad blocker. Please open in a new tab."
              : "PDF preview is not supported on mobile. Please open in a new tab."}
            fontWeight="font-normal"
            fontSize="text-sm"
            className="text-gray-600"
          />
        </div>
      )}
      {fetchingDocument && (
        <div className="flex h-[30vh] w-full justify-center items-center">
          <div className="animate-spin">
            <img
              src={require("../../assets/images/loadingPNG.png")}
              className={"h-[50px] w-[50px]"}
              alt="loading indicator"
            />
          </div>
        </div>
      )}
      {showTermsAndConditions && (
        <Formik
          initialValues={{
            firstName: profileData?.user?.firstName ?? "",
            email_id: profileData?.user?.email ?? "",
            lastName: profileData?.user?.lastName ?? "",
            acceptTerms: false,
            acceptTermsDate: moment().format("MM-DD-YYYY"),
          }}
          onSubmit={(data) => {
            mutation.mutate(data);
          }}
        >
          {({ values, errors, getFieldMeta, submitForm }) => {
            return (
              <div className="flex justify-between items-end mt-4 flex-wrap md:flex-nowrap">
                <CustomCheckBox
                  id="acceptTerms"
                  name="acceptTerms"
                  value={values.acceptTerms}
                  label="I have agreed to the Terms of Use."
                />
                <div className="flex items-center md:mt-0 mt-2">
                  <Button
                    textInformation={{
                      text: "Cancel",
                      fontWeight: "font-semibold",
                    }}
                    buttonTheme="secondary"
                    disabled={values.acceptTerms || updating || cancelLoading}
                    onClick={logout}
                    loading={cancelLoading}
                    className="mr-2"
                  />
                  <Button
                    textInformation={{
                      text: "Ok",
                      fontWeight: "font-semibold",
                    }}
                    buttonTheme="primary"
                    disabled={!values.acceptTerms || updating}
                    loading={updating}
                    onClick={submitForm}
                  />
                </div>
              </div>
            );
          }}
        </Formik>
      )}
    </div>
  );
}

export default TermsAndConditionsPopup;
